import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Form, Button, Alert, Spinner, InputGroup, ListGroup } from 'react-bootstrap';
import Select from 'react-select';
import { useLocation } from 'react-router-dom';
import moment from 'moment';
import 'moment/locale/it';
import { buildUrl } from '../helper/handler';
import { isValidEmail } from '../helper/validateEmail';
import { getFromApi, postToApi } from '../utilities/NetUtilities';
import { FaPaperPlane, FaPlus, FaTrash } from 'react-icons/fa';

moment.locale('it');

const getWeekRangeFromStartValue = (startValue) => {
    if (!startValue) return { start: '', end: '' };
    const startMoment = moment(startValue, 'DD MMMM YYYY', 'it');
    const endMoment = startMoment.clone().add(6, 'days');
    return {
        start: startMoment.format('YYYY-MM-DD'),
        end: endMoment.format('YYYY-MM-DD')
    };
};

const generateDefaultBody = (template, workerName, start, end) => {
    if (!template) return '';
    return template
        .replace('{nomeCognome}', workerName || 'Collaboratore');
}

function InviaOrarioLavoratore() {
    const location = useLocation();
    const { lavoratorePassato, settimanaPassata } = location.state || {};
    const [lavoratoreEmail, setLavoratoreEmail] = useState('');
    const [lavoratore, setLavoratore] = useState(null);
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [frequentEmails, setFrequentEmails] = useState([]);
    const [selectedFrequentEmails, setSelectedFrequentEmails] = useState([]);
    const [manualCcList, setManualCcList] = useState([]);
    const [currentManualCc, setCurrentManualCc] = useState('');
    const [ccErrorMessage, setCcErrorMessage] = useState('');
    const [emailBody, setEmailBody] = useState('');
    const [isBodyEdited, setIsBodyEdited] = useState(false);
    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState({});
    const [organizzazione, setOrganizzazione] = useState(null);
    const [templateToUse, setTemplateToUse] = useState();

    useEffect(() => {
        const organizzazione = JSON.parse(window.sessionStorage.getItem("organizzazione"));
        setOrganizzazione(organizzazione);

        const setMessageFromApi = (data) => {
            setMessage([{ tipo: data.messaggi[0].tipo, descrizione: data.messaggi[0].descrizione }]);
        }

        const setEmailFrequentiFromApi = (data) => {
            if (data.ok && data.emailfrequenti) {
                setFrequentEmails(data.emailfrequenti.map(email => ({ value: email, label: email })));
            }
        }

        const setTemplateFromApi = (data) => {
            if (data.ok && data.template) {
                const processedTemplate = data.template.replace(/\\n/g, '\n');
                setTemplateToUse(processedTemplate);
            }
        }

        const setDatiLavoratoreFromApi = (dataLavoratore) => {
            if (dataLavoratore.ok && dataLavoratore.lavoratoreCompleto) {
                setLavoratoreEmail(dataLavoratore.lavoratoreCompleto.email || '')
                if (!dataLavoratore.lavoratoreCompleto.email) {
                    setMessage({ tipo: 'warning', descrizione: 'Il lavoratore non ha un email. Inseriscila tramite il pannello di gestione dei lavoratori' });
                }
            }
        }

        if (!lavoratorePassato || !settimanaPassata) {
            setMessage({ tipo: 'danger', descrizione: 'Dati lavoratore o settimana mancanti.' });
            return;
        }

        const urlLavoratore = buildUrl('lavoratore', { lavoratore: lavoratorePassato.value });
        getFromApi(urlLavoratore, setDatiLavoratoreFromApi, setMessageFromApi);

        setLavoratore(lavoratorePassato);

        const { start, end } = getWeekRangeFromStartValue(settimanaPassata);
        setStartDate(start);
        setEndDate(end);

        if (organizzazione) {
            const urlEmails = buildUrl('getemailfrequenti', {
                organizzazione: organizzazione.nome,
                tipo: organizzazione.tipo
            });
            getFromApi(urlEmails, setEmailFrequentiFromApi, setMessageFromApi);

            const urlTemplate = buildUrl('getemailtemplate', {
                organizzazione: organizzazione.nome,
                tipo: organizzazione.tipo
            });
            getFromApi(urlTemplate, setTemplateFromApi, setMessageFromApi);
        }

    }, [lavoratorePassato, settimanaPassata]);

    useEffect(() => {
        if (!isBodyEdited && templateToUse && lavoratore && startDate && endDate) {
            setEmailBody(generateDefaultBody(templateToUse, lavoratore.label, startDate, endDate));
        }
    }, [startDate, endDate, templateToUse, lavoratore, isBodyEdited]);

    useEffect(() => {
        setCcErrorMessage('');
    }, [currentManualCc]);

    const handleStartDateChange = (e) => {
        setMessage({});
        setStartDate(e.target.value);
    };

    const handleEndDateChange = (e) => {
        setMessage({});
        setEndDate(e.target.value);
    };

    const handleFrequentEmailsChange = (selectedOptions) => {
        setMessage({});
        setSelectedFrequentEmails(selectedOptions || []);
    };

    const handleBodyChange = (e) => {
        setMessage({});
        setEmailBody(e.target.value);
        setIsBodyEdited(true);
    };

    const handleCurrentManualCcChange = (e) => {
        setMessage({});
        setCurrentManualCc(e.target.value);
    };

    const handleAddManualCc = () => {
        setMessage({});
        const emailToAdd = currentManualCc.trim();
        setCcErrorMessage('');

        if (!emailToAdd) return;
        if (!isValidEmail(emailToAdd)) {
            setCcErrorMessage('Formato email non valido.');
            return;
        }
        if (manualCcList.some(email => email.toLowerCase() === emailToAdd.toLowerCase()) ||
            selectedFrequentEmails.some(opt => opt.value.toLowerCase() === emailToAdd.toLowerCase())) {
            setCcErrorMessage('Indirizzo email già presente.');
            return;
        }
        setManualCcList(prevList => [...prevList, emailToAdd]);
        setCurrentManualCc('');
    };

    const handleCcInputKeyDown = (e) => {
        setMessage({});
        if (e.key === 'Enter') {
            e.preventDefault();
            handleAddManualCc();
        }
    };

    const handleDeleteManualCc = (emailToDelete) => {
        setMessage({});
        setManualCcList(prevList => prevList.filter(email => email !== emailToDelete));
    };

    const handleSendEmail = () => {

        setMessage({});
        if (!lavoratore || !lavoratoreEmail || !startDate || !endDate || !organizzazione) {
            setMessage({ tipo: 'danger', descrizione: 'Dati mancanti per inviare l\'email.' });
            return;
        }
        if (moment(endDate).isBefore(moment(startDate))) {
            setMessage({ tipo: 'danger', descrizione: 'La data di fine non può essere antecedente alla data di inizio.' });
            return;
        }

        setLoading(true);

        const frequentCc = selectedFrequentEmails.map(opt => opt.value);
        const allCcEmails = [...frequentCc, ...manualCcList];
        const uniqueCcList = [...new Set(allCcEmails)];

        const requestPayload = {
            lavoratoreId: lavoratore.value,
            startDate: moment(startDate).format('DD/MM/YYYY'),
            endDate: moment(endDate).format('DD/MM/YYYY'),
            ccList: uniqueCcList,
            body: emailBody,
            organizzazione: organizzazione.nome,
            tipoOrganizzazione: organizzazione.tipo
        };

        const okAction = (data) => {
            setLoading(false);
            if (data.ok) {
                setMessage({ tipo: data.messaggi[0].tipo, descrizione: data.messaggi[0].descrizione });
            }
        };

        const nokAction = (error) => {
            setLoading(false);
            setMessage({ tipo: error.messaggi[0].tipo, descrizione: error.messaggi[0].descrizione });
        };

        const url = buildUrl('inviaemailorariolavoratore');
        postToApi(url, requestPayload, okAction, nokAction);
    };

    return (
        <div className='main' role='main'>
            <Container>
                <h1 className="d-flex justify-content-center inEvidenza mb-4">Invia Orario Lavoratore</h1>

                {message.descrizione && (
                    <Alert variant={message.tipo} id="message">
                        {message.descrizione}
                    </Alert>
                )}

                {lavoratore ? (
                    <Form>
                        <Row className="mb-3">
                            <Col md={6}>
                                <Form.Group>
                                    <Form.Label className="inEvidenza">Lavoratore</Form.Label>
                                    <Form.Control type="text" id="nome_lavoratore" readOnly value={lavoratore.label || ''} />
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group>
                                    <Form.Label className="inEvidenza">Email Destinatario</Form.Label>
                                    <Form.Control type="email" id="email_lavoratore" readOnly value={lavoratoreEmail} />
                                </Form.Group>
                            </Col>
                        </Row>

                        <Row className="mb-3">
                            <Col md={6}>
                                <Form.Group>
                                    <Form.Label className="inEvidenza">Data Inizio</Form.Label>
                                    <Form.Control
                                        id="data_inizio"
                                        type="date"
                                        value={startDate}
                                        onChange={handleStartDateChange}
                                        required
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group>
                                    <Form.Label className="inEvidenza">Data Fine</Form.Label>
                                    <Form.Control
                                        id="data_fine"
                                        type="date"
                                        value={endDate}
                                        onChange={handleEndDateChange}
                                        required
                                    />
                                </Form.Group>
                            </Col>
                        </Row>
                        <Row className="mb-3">
                            <Col md={6}>
                                <Form.Group>
                                    <Form.Label className="inEvidenza">Email Frequenti (CC)</Form.Label>
                                    <Select isMulti name="frequentEmails" instanceId="frequentEmails" id="frequentEmails" options={frequentEmails} className="basic-multi-select" classNamePrefix="select" placeholder="Seleziona..." onChange={handleFrequentEmailsChange} value={selectedFrequentEmails} styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }} menuPortalTarget={document.body} />
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group>
                                    <Form.Label className="inEvidenza">Aggiungi Email (CC)</Form.Label>
                                    <InputGroup>
                                        <Form.Control
                                            id="email_manuale"
                                            type="email"
                                            placeholder="email"
                                            value={currentManualCc}
                                            onChange={handleCurrentManualCcChange}
                                            onKeyDown={handleCcInputKeyDown}
                                            aria-describedby="cc-error-message"
                                        />
                                        <Button
                                            variant="outline-secondary"
                                            onClick={handleAddManualCc}
                                            disabled={!currentManualCc.trim()}
                                            id="add-cc-btn"
                                            title="aggiungi"
                                            className="d-flex align-items-center mt-0 mr-0"
                                        >
                                            <FaPlus />
                                        </Button>
                                    </InputGroup>
                                    {ccErrorMessage && <div id="cc-error-message" className="text-danger mt-1" style={{ fontSize: '0.875em' }}>{ccErrorMessage}</div>}
                                </Form.Group>
                                {manualCcList.length > 0 && (
                                    <ListGroup variant="flush" className="mt-2 border rounded" style={{ maxHeight: '150px', overflowY: 'auto' }}>
                                        {manualCcList.map((email, index) => (
                                            <ListGroup.Item key={index} className="d-flex justify-content-between align-items-center py-1 px-2">
                                                <small id={`email-${index}`} style={{ wordBreak: 'break-all' }}>{email}</small>
                                                <Button variant="link" size="sm" onClick={() => handleDeleteManualCc(email)} aria-label={`Rimuovi ${email}`} className="text-danger p-0 ms-2">
                                                    <FaTrash />
                                                </Button>
                                            </ListGroup.Item>
                                        ))}
                                    </ListGroup>
                                )}
                            </Col>
                        </Row>

                        <Row className="mb-3">
                            <Col>
                                <Form.Group>
                                    <Form.Label className="inEvidenza">Testo Email</Form.Label>
                                    <Form.Control
                                        as="textarea"
                                        rows={8}
                                        value={emailBody}
                                        onChange={handleBodyChange}
                                        id="messaggio"
                                    />
                                </Form.Group>
                            </Col>
                        </Row>

                        <Row className="mt-4">
                            <Col className="d-flex justify-content-end">
                                <Button variant="primary" onClick={handleSendEmail} disabled={loading} className="btnGenerale" id="invia">
                                    {loading ? (
                                        <>
                                            <Spinner
                                                as="span"
                                                animation="border"
                                                size="sm"
                                                role="status"
                                                aria-hidden="true"
                                                className="me-1"
                                            />
                                            Invio...
                                        </>
                                    ) : (
                                        <>
                                            <FaPaperPlane className="me-1" /> Invia Email
                                        </>
                                    )}
                                </Button>
                            </Col>
                        </Row>
                    </Form>
                ) : (
                    <Alert variant="warning">Dati iniziali non disponibili. Torna alla pagina precedente e riprova.</Alert>
                )}
            </Container>
        </div>
    );
}

export default InviaOrarioLavoratore;